<template>
  <v-row
    class="px-4 pb-2"
    style="margin: 0; margin-left: -12px; margin-right: -12px"
  >
    <v-col class="py-0">
      <v-autocomplete
        hide-details
        :items="orderedDuaCancellationCodes"
        item-text="text"
        item-value="code"
        label="Inställelse"
        :loading="$apollo.loading"
        v-model="dua_cancellation_code"
        :rules="[!isChecked && validatingForm ? () => true : required]"
        :disabled="item.missing || isHistoryEvent"
        :append-icon="
          isDiff('dua_cancellation_code')
            ? 'mdi-alert-octagram'
            : 'mdi-menu-down'
        "
      />
    </v-col>
    <v-col class="py-0">
      <v-autocomplete
        hide-details
        label="Hänvisning"
        v-model="dua_reference_code"
        :items="orderedDuaReferenceCodes"
        item-text="text"
        item-value="code"
        :loading="$apollo.loading"
        clearable
        :disabled="item.missing || isHistoryEvent"
        :append-icon="
          isDiff('dua_reference_code') ? 'mdi-alert-octagram' : 'mdi-menu-down'
        "
      />
    </v-col>
    <v-col class="py-0" cols="2">
      <v-text-field
        label="Beräknad"
        type="time"
        :disabled="
          cancellationCodeInstallt === dua_cancellation_code || isHistoryEvent
        "
        :rules="[
          !isChecked && validatingForm
            ? () => true
            : estimatedTimeAtLocationRule
        ]"
        :append-icon="
          isDiff('estimated_time_at_location')
            ? 'mdi-alert-octagram'
            : 'mdi-menu-down'
        "
        v-model="estimated_time_at_location"
      />
    </v-col>
    <DueMessageRouteField
      v-if="showRouteField"
      :item="item"
      :uuid="uuid"
      :validating-form="validatingForm"
    />
    <v-col cols="1"> </v-col>
    <!-- <v-col class="py-0">
      <v-autocomplete
        hide-details
        v-model="dua_reason_code"
        label="Orsak"
        :items="orderedDuaReasonCodes"
        item-text="text"
        item-value="code"
        clearable
        :loading="$apollo.loading"
        :disabled="item.missing || isHistoryEvent"
        :append-icon="
          isDiff('dua_reason_code') ? 'mdi-alert-octagram' : 'mdi-menu-down'
        "
      />
    </v-col> -->
  </v-row>
</template>

<script>
import { required } from '@/common/validationRules'
import gql from 'graphql-tag'
import { get } from 'vuex-pathify'
import orderBy from 'lodash.orderby'
import {
  format,
  isAfter,
  addHours,
  isBefore,
  addDays,
  differenceInMinutes
} from 'date-fns'
import DueMessageRouteField from './DuaMessageRouteField'

const INVÄNTA_INFO_CODE = 'ANA075'
const INSTÄLLT_CANCELATION_CODE = 'ANA027'

const duaCancellationCodesOrder = [
  INSTÄLLT_CANCELATION_CODE,
  'ANA007',
  'ANA058'
]
const duaReferenceCodesOrder = ['ANA039', INVÄNTA_INFO_CODE]

const getSet = prop => ({
  get() {
    return this.form[this.uuid].data[prop]
  },

  set(v) {
    let value = v
    if (!value || !value.length) {
      value = null
    }
    const form = Object.assign({}, this.form)

    if (prop === 'dua_cancellation_code') {
      form[this.uuid].data.replacement_from_place = null
      form[this.uuid].data.replacement_to_place = null
    }

    form[this.uuid].data[prop] = value
    this.$store.dispatch('duaMessage/updateForm', {
      uuid: this.uuid,
      prop,
      value
    })
  }
})

const getSetDateTime = prop => ({
  get() {
    const dateTime = this.form[this.uuid].data[prop]
    if (dateTime) return format(new Date(dateTime), 'HH:mm')
    return null
  },

  set(v) {
    let value = v
    const form = Object.assign({}, this.form)
    if (!value || !value.length) {
      form[this.uuid].data[prop] = null
    } else {
      const date = format(new Date(), 'yyyy-MM-dd')

      form[this.uuid].data[prop] = new Date(
        format(new Date(`${date} ${value}`), 'yyyy-MM-dd HH:mm')
      )
      if (isBefore(form[this.uuid].data[prop], this.departureDate)) {
        const addDay = addDays(new Date(), 1)
        const addDayDate = format(addDay, 'yyyy-MM-dd')
        form[this.uuid].data[prop] = new Date(
          format(new Date(`${addDayDate} ${value}`), 'yyyy-MM-dd HH:mm')
        )
      }
    }
    this.$store.dispatch('duaMessage/updateForm', {
      uuid: this.uuid,
      prop,
      value: form[this.uuid].data[prop]
    })
  }
})

export default {
  name: 'DuaMessageCancellationFields',

  components: {
    DueMessageRouteField
  },

  props: {
    item: Object,
    uuid: String,
    missing: Boolean,
    validatingForm: Boolean
  },

  data: () => ({
    cancellationCodeInstallt: INSTÄLLT_CANCELATION_CODE,
    invantaInfoCode: INVÄNTA_INFO_CODE,
    required
  }),

  computed: {
    model: get('duaMessage/model'),
    form: get('duaMessage/form'),
    isHistoryEvent: get('history/isHistoryEvent'),

    showRouteField() {
      return ['CANCELED_STOP', 'CANCELED', 'PARTIAL_CANCELLATION'].includes(
        this.item.text.deviation_type
      )
    },

    isChecked() {
      return this.form[this.uuid]._checked
    },

    departureDate() {
      return new Date(this.item.text.departure)
    },

    orderedDuaCancellationCodes() {
      return this.duaCancellationCodes
        ? duaCancellationCodesOrder.map(code =>
            this.duaCancellationCodes.find(x => x.code === code)
          )
        : []
    },

    orderedDuaReferenceCodes() {
      const codes = this.duaReferenceCodes
        ? duaReferenceCodesOrder.map(code =>
            this.duaReferenceCodes.find(x => x.code === code)
          )
        : []

      if (this.dua_cancellation_code !== INSTÄLLT_CANCELATION_CODE) {
        return codes.filter(x => x.code === INVÄNTA_INFO_CODE)
      }

      return codes
    },

    orderedDuaReasonCodes() {
      return orderBy(this.duaReasonCodes || [], ['text'], ['asc'])
    },

    currentTrip() {
      return this.model?.body?.find(x => x?.trip_uuid === this.uuid)
    },

    dua_cancellation_code: {
      ...getSet('dua_cancellation_code')
    },

    dua_reason_code: {
      ...getSet('dua_reason_code')
    },

    dua_reference_code: {
      ...getSet('dua_reference_code')
    },

    estimated_time_at_location: {
      ...getSetDateTime('estimated_time_at_location')
    }
  },

  watch: {
    dua_cancellation_code() {
      this.dua_reference_code = null
      this.estimated_time_at_location = null
    }
  },

  methods: {
    isDiff(prop) {
      if (!this.currentTrip) return false
      const currentProp = this.currentTrip.data[prop] || null
      const formProp = this.form[this.uuid].data[prop] || null
      if (currentProp && formProp && prop === 'estimated_time_at_location') {
        return (
          format(new Date(currentProp), 'yyyy-MM-dd HH:mm') !==
          format(new Date(formProp), 'yyyy-MM-dd HH:mm')
        )
      }
      return currentProp !== formProp
    },

    estimatedTimeAtLocationRule() {
      if (
        this.cancellationCodeInstallt === this.dua_cancellation_code ||
        this.missing
      ) {
        return true
      }

      if (!this.form[this.uuid].data.estimated_time_at_location) {
        return true
      }

      const eta = new Date(this.form[this.uuid].data.estimated_time_at_location)

      const minDiffEtaAndDepartureDate = differenceInMinutes(
        eta,
        this.departureDate
      )

      if (
        isAfter(eta, new Date()) &&
        (isAfter(eta, this.departureDate) ||
          minDiffEtaAndDepartureDate === 0) &&
        isBefore(eta, addHours(this.departureDate, 16))
      ) {
        return true
      }
      return 'Måste vara efter avgångstid samt högst vara 16 timmar efter avg.'
    }
  },

  apollo: {
    duaCancellationCodes: {
      query: gql`
        query duaCancellationCodes {
          duaCancellationCodes {
            code
            text
          }
        }
      `
    },
    duaReferenceCodes: {
      query: gql`
        query duaReferenceCodes {
          duaReferenceCodes {
            code
            text
          }
        }
      `
    },
    duaReasonCodes: {
      query: gql`
        query duaReasonCodes {
          duaReasonCodes {
            code
            text
          }
        }
      `
    }
  }
}
</script>
